import { msg } from "@lingui/core/macro";
import { createRoute, useSearch } from "@tanstack/solid-router";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import {
  CheckCircle2,
  Filter,
  Mail as MailIcon,
  ShieldCheck,
  Trash2,
  TriangleAlert,
} from "@fleet/icons";

import { appRoute } from "./route";
import { AuthPlaceholder } from "@/components/auth-placeholder";
import { Badge, Card, CardContent, PageContainer, PageHeader } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";
import { queryClient } from "@/lib/query-client";
import { ConnectAccountForm } from "@/features/mail/components/connect-account-form";
import { MailAuditLog } from "@/features/mail/components/audit-log";
import { RulesEditor } from "@/features/mail/components/rules-editor";
import { useDeleteAccount, useMailAccounts } from "@/features/mail/hooks";
import { mailKeys } from "@/features/mail/api";
import { type MailSettingsSearch, validateMailSettingsSearch } from "@/features/mail/route-search";

function MailSettingsPage() {
  const { t } = useI18n();
  const accounts = useMailAccounts();
  const deleteMutation = useDeleteAccount();
  const [showConnect, setShowConnect] = createSignal(false);
  const search = useSearch({ from: "/_app/mail/settings" }) as () => MailSettingsSearch;

  // Landing back from the provider with `?oauth=connected`: the server-side
  // callback just created the account, so drop the cached list and refetch.
  createEffect(() => {
    if (search().oauth === "connected") {
      void queryClient.invalidateQueries({ queryKey: mailKeys.accounts() });
    }
  });

  // Onboarding: landing here with NO connected account (e.g. via the mailbox
  // "Connect an account" CTA) opens the connect form directly — a second
  // "Add account" click would be a dead stop for a brand-new user.
  createEffect(() => {
    if (!accounts.isLoading && (accounts.data?.length ?? 0) === 0) {
      setShowConnect(true);
    }
  });

  const activeRulesAccount = createMemo(() => accounts.data?.[0]?.id);

  return (
    <AuthPlaceholder
      title={t(msg`Mail settings`)}
      description={t(msg`Sign in to connect mailboxes and manage filter rules.`)}
    >
      <PageContainer maxWidth="3xl">
        <PageHeader title={t(msg`Mail settings`)} />

        <Show when={search().oauth === "connected"}>
          <div
            class="mb-4 flex items-center gap-2 rounded-lg border border-border/50 bg-card/30 px-4 py-3 text-sm"
            role="status"
            data-testid="mail-oauth-connected"
          >
            <CheckCircle2 class="h-4 w-4 shrink-0 text-primary" />
            <span>
              {t(msg`Account connected`)}
              <Show when={search().email}> · {search().email}</Show>
            </span>
          </div>
        </Show>
        <Show when={search().oauth === "error"}>
          <div
            class="mb-4 flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            role="alert"
            data-testid="mail-oauth-error"
          >
            <TriangleAlert class="h-4 w-4 shrink-0" />
            <span>
              {t(msg`Could not connect the account. Please try again.`)}
              <Show when={search().reason}> ({search().reason})</Show>
            </span>
          </div>
        </Show>

        <section class="mb-8" data-testid="mail-accounts-section">
          <h2 class="mb-3 text-sm font-semibold text-foreground">{t(msg`Connected accounts`)}</h2>
          <Show
            when={(accounts.data?.length ?? 0) > 0}
            fallback={
              <p class="mb-3 text-sm text-muted-foreground">{t(msg`No accounts connected yet.`)}</p>
            }
          >
            <ul class="mb-3 flex flex-col gap-2">
              <For each={accounts.data}>
                {(a) => (
                  <li
                    class="flex items-center gap-3 rounded-lg border border-border/50 bg-card/30 px-4 py-3"
                    data-testid="mail-account-item"
                  >
                    <MailIcon class="h-4 w-4 text-muted-foreground" />
                    <div class="min-w-0 flex-1">
                      <p class="flex items-center gap-2 truncate text-sm font-medium">
                        <span class="truncate">{a.displayName?.trim() || a.email}</span>
                        <Show when={a.orgId}>
                          <Badge variant="secondary" data-testid="mail-account-team-badge">
                            {t(msg`Team`)}
                          </Badge>
                        </Show>
                      </p>
                      <p class="truncate text-xs text-muted-foreground/70">
                        {a.email} · {a.imapHost}:{a.imapPort}
                      </p>
                    </div>
                    <button
                      type="button"
                      class="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => deleteMutation.mutate(a.id)}
                      aria-label={t(msg`Remove account`)}
                      data-testid="mail-account-remove"
                    >
                      <Trash2 class="h-4 w-4" />
                    </button>
                  </li>
                )}
              </For>
            </ul>
          </Show>

          <Show
            when={showConnect()}
            fallback={
              <Button
                variant="outline"
                onClick={() => setShowConnect(true)}
                data-testid="mail-add-account"
              >
                {t(msg`Add account`)}
              </Button>
            }
          >
            <Card>
              <CardContent class="pt-5">
                <ConnectAccountForm onConnected={() => setShowConnect(false)} />
              </CardContent>
            </Card>
          </Show>
        </section>

        <Show when={activeRulesAccount()}>
          {(id) => (
            <>
              <section data-testid="mail-rules-section">
                <h2 class="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Filter class="h-4 w-4" /> {t(msg`Filter rules`)}
                </h2>
                <RulesEditor accountId={id()} />
              </section>

              <section class="mt-8" data-testid="mail-audit-section">
                <h2 class="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                  <ShieldCheck class="h-4 w-4" /> {t(msg`Access audit`)}
                </h2>
                <p class="mb-3 text-xs text-muted-foreground">
                  {t(
                    msg`Every use of this mailbox — syncs, sends, exports and rule changes — is recorded here.`,
                  )}
                </p>
                <MailAuditLog accountId={id()} />
              </section>
            </>
          )}
        </Show>
      </PageContainer>
    </AuthPlaceholder>
  );
}

export const mailSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/mail/settings",
  component: MailSettingsPage,
  validateSearch: validateMailSettingsSearch,
});
